import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Calendar, Lock, Eye, EyeOff, X, MapPin, Phone, FileText, Heart, Users } from 'lucide-react';
import '../styles/Profile.css';
import axiosInstance from '../api/api.js';
import { useAuth } from '../components/AuthContext.js';

const Profile = () => {
  const { id } = useAuth();

  const [userData, setUserData] = useState({
    id: '',
    name: '',
    email: '',
    role: '',
    position: '',
    phone: '',
    address: '',
    birthDate: '',
    description: '',
    is_active: true,
    created_at: '',
    updated_at: '',
    // Additional fields from your API
    start_date: '',
    end_date: '',
    nationality: '',
    ni_number: '',
    gp_name: '',
    gp_address: '',
    gp_phone: '',
    next_of_kin: '',
    next_of_kin_address: '',
    next_of_kin_phone: '',
    next_of_kin_relation: '',
    special_needs: '',
    salary: ''
  });

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/employees/${id}`);
        const data = response.data;

        // Map the API response to your component's expected structure
        setUserData({
          id: data.id || data.user_id,
          name: data.user?.name || data.name || '',
          email: data.user?.email || data.email || '',
          role: data.user?.role || 'employee',
          position: data.position || '',
          phone: data.phone || '',
          address: data.address || '',
          birthDate: data.birth_date || '',
          description: data.description || '',
          is_active: data.user?.is_active || data.is_active || true,
          created_at: data.createdAt || data.created_at || '',
          updated_at: data.updatedAt || data.updated_at || '',
          // Additional fields
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          nationality: data.nationality || '',
          ni_number: data.ni_number || '',
          gp_name: data.gp_name || '',
          gp_address: data.gp_address || '',
          gp_phone: data.gp_phone || '',
          next_of_kin: data.next_of_kin || '',
          next_of_kin_address: data.next_of_kin_address || '',
          next_of_kin_phone: data.next_of_kin_phone || '',
          next_of_kin_relation: data.next_of_kin_relation || '',
          special_needs: data.special_needs || '',
          salary: data.salary || ''
        });
      } catch (err) {
        console.error(err);
        setError('Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [id]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("New passwords don't match");
      return;
    }

    if (passwordData.newPassword !== passwordData.currentPassword) {
      setError("current password don't match");
      return;
    }

    if (passwordData.newPassword === passwordData.currentPassword) {
      setError("New password cannot be the same as current password");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (!passwordData.currentPassword) {
      setError("Current password is required");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(
        '/auth/change-password',
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        }
      );

      if (response.data.success) {
        setMessage(response.data.message || 'Password changed successfully');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordModal(false);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(response.data.error || 'Something went wrong');
      }

    } catch (err) {
      setError('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowPasswordModal(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading && !userData.id) {
    return (
      <div className="profile-loading">
        <div className="profile-spinner"></div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-content">
        {/* Header */}
        <div className="profile-header">
          <div className="profile-header-content">
            <div className="profile-header-info">
              <div className="profile-avatar">
                <User className="profile-avatar-icon" />
              </div>
              <div className="profile-header-details">
                <div className="profile-name-container">
                  <h1 className="profile-name">{userData.name}</h1>
                </div>
                <p className="profile-role">{userData.position}</p>
                <div className="profile-status">
                  <div className={`profile-status-indicator ${userData.is_active ? 'active' : 'inactive'}`}></div>
                  <span className={`profile-status-text ${userData.is_active ? 'active' : 'inactive'}`}>
                    {userData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              {/* <button
                onClick={() => setShowPasswordModal(true)}
                className="profile-change-password-btn"
              >
                <Lock className="profile-change-password-icon" />
                Change Password
              </button> */}
            </div>
          </div>
        </div>



        {/* Personal Information */}
        <div className="profile-section">
          <div className="profile-section-header">
            <h2 className="profile-section-title">
              <User className="profile-section-icon" />
              Personal Information
            </h2>
          </div>

          <div className="profile-section-body">
            <div className="profile-info-list">
              <div className="profile-info-row">
                <div className="profile-info-label">
                  <User className="profile-info-icon" />
                  Full Name
                </div>
                <div className="profile-info-value">{userData.name}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Mail className="profile-info-icon" />
                  Email Address
                </div>
                <div className="profile-info-value">{userData.email}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Phone className="profile-info-icon" />
                  Phone
                </div>
                <div className="profile-info-value">{userData.phone}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <MapPin className="profile-info-icon" />
                  Address
                </div>
                <div className="profile-info-value">{userData.address}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Calendar className="profile-info-icon" />
                  Birth Date
                </div>
                <div className="profile-info-value">
                  {formatDate(userData.birthDate)}
                </div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <FileText className="profile-info-icon" />
                  Nationality
                </div>
                <div className="profile-info-value">{userData.nationality || 'Not specified'}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Shield className="profile-info-icon" />
                  NI Number
                </div>
                <div className="profile-info-value">{userData.ni_number || 'Not specified'}</div>
              </div>
              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Shield className="profile-info-icon" />
                  Salary
                </div>
                <div className="profile-info-value">{userData.salary || 'Not specified'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="profile-section">
          <div className="profile-section-header">
            <h2 className="profile-section-title">
              <Shield className="profile-section-icon" />
              Professional Information
            </h2>
          </div>

          <div className="profile-section-body">
            <div className="profile-info-list">
              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Shield className="profile-info-icon" />
                  Position
                </div>
                <div className="profile-info-value">{userData.position}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Calendar className="profile-info-icon" />
                  Start Date
                </div>
                <div className="profile-info-value">
                  {formatDate(userData.start_date)}
                </div>
              </div>

              {userData.end_date && (
                <div className="profile-info-row">
                  <div className="profile-info-label">
                    <Calendar className="profile-info-icon" />
                    End Date
                  </div>
                  <div className="profile-info-value">
                    {formatDate(userData.end_date)}
                  </div>
                </div>
              )}

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <FileText className="profile-info-icon" />
                  Description
                </div>
                <div className="profile-info-value">{userData.description}</div>
              </div>

              {userData.special_needs && userData.special_needs !== 'None' && (
                <div className="profile-info-row">
                  <div className="profile-info-label">
                    <Heart className="profile-info-icon" />
                    Special Needs
                  </div>
                  <div className="profile-info-value">{userData.special_needs}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Medical Information */}
        <div className="profile-section">
          <div className="profile-section-header">
            <h2 className="profile-section-title">
              <Heart className="profile-section-icon" />
              Medical Information
            </h2>
          </div>

          <div className="profile-section-body">
            <div className="profile-info-list">
              <div className="profile-info-row">
                <div className="profile-info-label">
                  <User className="profile-info-icon" />
                  GP Name
                </div>
                <div className="profile-info-value">{userData.gp_name || 'Not specified'}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <MapPin className="profile-info-icon" />
                  GP Address
                </div>
                <div className="profile-info-value">{userData.gp_address || 'Not specified'}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Phone className="profile-info-icon" />
                  GP Phone
                </div>
                <div className="profile-info-value">{userData.gp_phone || 'Not specified'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="profile-section">
          <div className="profile-section-header">
            <h2 className="profile-section-title">
              <Users className="profile-section-icon" />
              Emergency Contact
            </h2>
          </div>

          <div className="profile-section-body">
            <div className="profile-info-list">
              <div className="profile-info-row">
                <div className="profile-info-label">
                  <User className="profile-info-icon" />
                  Next of Kin
                </div>
                <div className="profile-info-value">{userData.next_of_kin || 'Not specified'}</div>
              </div>
              <div className="profile-info-row">
                <div className="profile-info-label">
                  <User className="profile-info-icon" />
                  Relation
                </div>
                <div className="profile-info-value">{userData.next_of_kin_relation || 'Not specified'}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <MapPin className="profile-info-icon" />
                  Address
                </div>
                <div className="profile-info-value">{userData.next_of_kin_address || 'Not specified'}</div>
              </div>

              <div className="profile-info-row">
                <div className="profile-info-label">
                  <Phone className="profile-info-icon" />
                  Phone
                </div>
                <div className="profile-info-value">{userData.next_of_kin_phone || 'Not specified'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Password Modal */}
        {showPasswordModal && (
          <div className="profile-modal-overlay">
            <div className="profile-modal">
              <div className="profile-modal-header">
                <h3 className="profile-modal-title">
                  <Lock className="profile-modal-title-icon" />
                  Change Password
                </h3>
                <button onClick={closeModal} className="profile-modal-close">
                  <X className="profile-modal-close-icon" />
                </button>
              </div>

              {/* Alerts */}
              {message && (
                <div className="profile-alert success">
                  <div className="profile-alert-content">
                    <div className="profile-alert-icon success">
                      <svg className="profile-alert-check" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="profile-alert-message">{message}</span>
                  </div>
                  <button onClick={() => setMessage('')} className="profile-alert-close">
                    <X className="profile-alert-close-icon" />
                  </button>
                </div>
              )}

              {error && (
                <div className="profile-alert error">
                  <div className="profile-alert-content">
                    <div className="profile-alert-icon error">
                      <X className="profile-alert-x-icon" />
                    </div>
                    <span className="profile-alert-message">{error}</span>
                  </div>
                  <button onClick={() => setError('')} className="profile-alert-close">
                    <X className="profile-alert-close-icon" />
                  </button>
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="profile-modal-body">
                {['currentPassword', 'newPassword', 'confirmPassword'].map((field, i) => {
                  const label =
                    field === 'currentPassword'
                      ? 'Current Password'
                      : field === 'newPassword'
                        ? 'New Password'
                        : 'Confirm New Password';
                  const show = showPasswords[field.replace('Password', '')];
                  return (
                    <div className="profile-password-field" key={i}>
                      <label className="profile-password-label">{label}</label>
                      <div className="profile-password-input-container">
                        <input
                          type={show ? 'text' : 'password'}
                          name={field}
                          value={passwordData[field]}
                          onChange={handlePasswordChange}
                          className="profile-password-input"
                          placeholder={`Enter ${label.toLowerCase()}`}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            togglePasswordVisibility(field.replace('Password', ''))
                          }
                          className="profile-password-toggle"
                        >
                          {show ? <EyeOff /> : <Eye />}
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="profile-modal-actions">
                  <button
                    type="submit"
                    disabled={loading}
                    className="profile-modal-submit"
                  >
                    {loading ? (
                      <div className="profile-modal-spinner"></div>
                    ) : (
                      <>
                        <Lock className="profile-modal-submit-icon" />
                        Change Password
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={loading}
                    className="profile-modal-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
